import { useEffect, useRef, FC, useMemo } from "react";
import {
  Button,
  Chip,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AddFirst,
  AddLast,
  CutAction,
  DeleteAction,
  DeleteAudioTrack,
  GetEditMode,
  GetFps,
  GetTimelineActions,
  MoveAudioTrack,
  SetLinkedToPreviewActionId,
  SetPreviewIsPlaying,
  SetPreviewSrc,
  SetPreviewTime,
  SetSelectedAsset,
  SetSelectedAssetFile,
  SetShowSequence,
} from "../../Redux/Reducers/redux-slice-timetool";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Thumbnail } from "@remotion/player";
import { cacheService, hlsChunkerService, mediaService } from "../../Core/Api/services";
import { CustomTimelineAction } from "./custom-timeline-action";
import { CusTomTimelineRow } from "./custom-timeline-row";
import DeleteIcon from "@mui/icons-material/Delete";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { HlsVideo } from "./PausableVideo";
import { DeleteOutlineOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const trackIndexColors = ["#D6D6EA", "#BCBCDC", "#A1A1CE", "#8787C0", "#6C6CB2", "#5454A0", "#464686", "#38386B"];
const ActionPopper: FC<{
  setFocusActionId: React.Dispatch<React.SetStateAction<string>>;
  actionId: string;
  anchorEl: HTMLDivElement;
}> = ({ setFocusActionId, actionId, anchorEl }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const loadedAssets = useSelector(GetTimelineActions)
    .map((x) => x.actions.map((a) => ({ ...a.data, start: a.start, end: a.end, actionId: a.id })))
    .flat(1);

  const handlePreviewAction = () => {
    const id = parseFloat(loadedAssets.find((x) => x.actionId === actionId)?.id || "-1");
    if (!id || id === -1) return;

    mediaService.GetFilesByObjectId(id).then((res) => {
      dispatch(SetShowSequence(false));
      const selectedFile = res.files?.find((x) => x.category === "Proxy");

      if (!selectedFile) return;
      dispatch(SetSelectedAssetFile(selectedFile));
      dispatch(SetSelectedAsset(loadedAssets.find((x) => x.id === id.toString())));
      dispatch(
        SetPreviewSrc(
          selectedFile?.id
            ? `${hlsChunkerService.GetServiceUrl()}/${selectedFile.id}/${cacheService.appData?.platformId}`
            : ""
        )
      );
      dispatch(SetPreviewIsPlaying(false));
      dispatch(SetPreviewTime(0));
      dispatch(SetLinkedToPreviewActionId(actionId?.toString() || ""));
    });
  };
  return (
    <ClickAwayListener
      onClickAway={(e) => {
        if (
          (e.target as any)?.id !== "audioTrack_button" &&
          (e.target as any)?.farthestViewportElement?.id !== "audioTrack_button"
        )
          setFocusActionId("");
      }}
    >
      <Popper
        anchorEl={anchorEl}
        open={true}
        placement="right"
        modifiers={[
          {
            name: "flip",
            enabled: true,
            options: {
              altBoundary: true,
              rootBoundary: "document",
              padding: 8,
            },
          },
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              rootBoundary: "document",
              padding: 8,
            },
          },
        ]}
      >
        <Paper>
          <Stack p={0.5} display="flex" direction="row">
            {/* <Tooltip title="Move up">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(ChangeActionRow({ actionId: actionId, direction: "up" }));
                }}
                key="move-up"
              >
                <ArrowUpwardIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Move down">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(ChangeActionRow({ actionId: actionId, direction: "down" }));
                }}
                key="move-down"
              >
                <ArrowDownwardIcon />
              </IconButton>
            </Tooltip> */}
            <Tooltip title={t("tooltip:title.moveToFirstPosition")}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(AddFirst(actionId));
                }}
                key="add-first"
              >
                <FirstPageIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("tooltip:title.moveToLastPosition")}>
              <IconButton
                sx={{ transform: "rotate(180deg)" }}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(AddLast(actionId));
                }}
                key="add-last"
              >
                <FirstPageIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("common:preview")}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handlePreviewAction();
                }}
                key="preview-action"
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("tooltip:title.deleteClip")}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(DeleteAction(actionId));
                }}
                key="move-delete"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>
      </Popper>
    </ClickAwayListener>
  );
};

export const TimelineAction: FC<{
  action: CustomTimelineAction;
  row: CusTomTimelineRow;
  focus: boolean;
  setFocusActionId: React.Dispatch<React.SetStateAction<string>>;
  selected: boolean;
  timelineWidth: number;
  timelineScale: number;
  inFrame: number | null;
  outFrame: number | null;
  verticalScale: number;
}> = ({
  action,
  row,
  focus,
  setFocusActionId,
  selected,
  timelineWidth,
  timelineScale,
  inFrame,
  outFrame,
  verticalScale,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const dispatch = useDispatch();

  const fps = useSelector(GetFps);
  const editMode = useSelector(GetEditMode);

  const handleContextMenuClick = (e) => {
    e.preventDefault();
    setFocusActionId(action.id);
  };

  const isAudio =
    action.data.audioTrackIndex !== null &&
    action.data.audioTrackIndex !== undefined &&
    action.data.audioMapping !== null &&
    action.data.audioMapping !== undefined;
  const audioIndex = isAudio
    ? action.data.audioMapping!.findIndex((x) => x.trackNumber === action.data.audioTrackInfos?.trackNumber)
    : -1;
  const thumb = useMemo(
    () =>
      isAudio ? (
        <></>
      ) : (
        <Thumbnail
          durationInFrames={1}
          frameToDisplay={1}
          fps={fps}
          style={{ height: "100%", marginLeft: "3px", borderRadius: "6px" }}
          compositionHeight={9}
          compositionWidth={16}
          component={() => <HlsVideo startFrom={Math.round(action.data.offSet)} src={`${action.data.src}` || ""} />}
        />
      ),
    [action.data.offSet, action.data.src, action.data.audioTrackIndex]
  );

  useEffect(() => {
    !isAudio && ref.current?.addEventListener("contextmenu", handleContextMenuClick);
    return () => {
      !isAudio && ref.current?.removeEventListener("contextmenu", handleContextMenuClick);
    };
  }, [ref.current, isAudio]);

  const tooltipTitle = useMemo(() => {
    if (isAudio)
      return `A ${action.data.audioTrackInfos!.trackNumber}
                ${action.data.audioTrackInfos?.channelPosition && ` - ${action.data.audioTrackInfos.channelPosition}`} 
                ${
                  action.data.audioTrackInfos?.language?.value ? ` - ${action.data.audioTrackInfos.language.value}` : ""
                }`;
    else return action.data.name;
  }, [isAudio, action?.id, action.data?.audioTrackInfos?.trackNumber]);
  return (
    <Tooltip title={tooltipTitle + (isAudio ? ` - ${action.data.audioMix}` : "")}>
      <div
        ref={ref}
        style={{
          userSelect: "none",
          height: `${verticalScale}px`,
          alignContent: "center",
          alignItems: "center",
          textOverflow: "ellipsis",
          display: "flex",
          backgroundColor: theme.palette.background.paper,
        }}
        className={"effect0"}
      >
        <div
          style={{
            userSelect: "none",
            border: focus && !isAudio ? `2px solid #2D6CE1` : "none",
            height: `${verticalScale * 0.9}px`,
            textOverflow: "ellipsis",
            width: "100%",
            display: "flex",
            borderRadius: "4px",

            backgroundColor:
              isAudio &&
              action.data.audioTrackInfos !== null &&
              action.data.audioTrackInfos !== undefined &&
              action.data.audioTrackInfos.trackNumber !== null
                ? trackIndexColors[
                    Math.trunc((action.data.audioTrackInfos.trackNumber - 1) / 2) % trackIndexColors.length
                  ]
                : selected
                ? theme.palette.primary.alphaHover
                : theme.palette.grey[800],
          }}
          onClick={(e) => {
            if (editMode !== "cut") return;

            const target = e.currentTarget;
            const rect = target.getBoundingClientRect();

            const x = e.clientX - rect.left;
            dispatch(
              CutAction({
                actionId: action.id,
                cut: action.start + (x / (rect.right - rect.left)) * (action.end - action.start),
              })
            );
            e.preventDefault();
          }}
        >
          {!isAudio && thumb}
          <div
            style={{
              whiteSpace: "nowrap",
              lineHeight: "100%",
              height: "100%",
              width: "-webkit-fill-available",
              overflow: "hidden",
              textOverflow: "ellipsis",
              userSelect: "none",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignContent: "center",
              alignItems: "center",
              marginLeft: "3px",
              color: theme.palette.getContrastText(
                isAudio &&
                  action.data.audioTrackInfos !== null &&
                  action.data.audioTrackInfos !== undefined &&
                  action.data.audioTrackInfos.trackNumber !== null
                  ? trackIndexColors[
                      Math.trunc((action.data.audioTrackInfos.trackNumber - 1) / 2) % trackIndexColors.length
                    ]
                  : selected
                  ? theme.palette.primary.text ?? theme.palette.grey[800]
                  : theme.palette.grey[800]
              ),
            }}
            className={`effect0-text`}
          >
            {!isAudio && <Typography sx={{ userSelect: "none" }}> {action.effectId}</Typography>}
            {isAudio && (
              <Stack justifyContent={"space-between"} width={"100px"} ml={1} direction={"row"} spacing={2}>
                <Chip
                  size="small"
                  color={audioIndex === action.data.audioTrackInfos?.inputIndex ? "info" : "warning"}
                  label={tooltipTitle}
                />
              </Stack>
            )}
            {isAudio && focus && (
              <Stack spacing={0.5} direction={"row"}>
                {audioIndex > 0 && (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    id="audioTrack_button"
                    onClick={() => {
                      dispatch(MoveAudioTrack({ actionId: action.data.actionId!, direction: "up", index: audioIndex }));
                    }}
                  >
                    <ArrowUpwardIcon fontSize="small" id="audioTrack_button" />
                  </Button>
                )}
                {audioIndex < action.data.audioMapping!.length - 1 && (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    id="audioTrack_button"
                    onClick={() => {
                      dispatch(
                        MoveAudioTrack({ actionId: action.data.actionId!, direction: "down", index: audioIndex })
                      );
                    }}
                  >
                    <ArrowDownwardIcon fontSize="small" id="audioTrack_button" />
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  id="audioTrack_button"
                  onClick={() => {
                    dispatch(DeleteAudioTrack({ actionId: action.data.actionId!, index: audioIndex }));
                  }}
                >
                  <DeleteOutlineOutlined fontSize="small" id="audioTrack_button" />
                </Button>
              </Stack>
            )}
          </div>
          {focus && !isAudio && ref.current && (
            <ActionPopper anchorEl={ref.current} actionId={action.id} setFocusActionId={setFocusActionId} />
          )}
        </div>
      </div>
    </Tooltip>
  );
};
